import {
  BaseSeason,
  DEFAULT_REFEREE_PAYMENTS_CONFIG,
  Match,
  RefereePaymentsConfig,
  TenantConfig,
} from '@models';

/**
 * Resolves the effective referee payments configuration applying the order
 * Season → Tenant → defaults (R4, R5).
 *
 * - The season override (`season.refereePaymentsConfig`) is partial: only its
 *   defined fields take precedence.
 * - The tenant config (`tenantConfig.refereePaymentsConfig`) fills the gaps.
 * - `DEFAULT_REFEREE_PAYMENTS_CONFIG` provides the final fallback so the result
 *   is always a complete `RefereePaymentsConfig`.
 *
 * Pure function — reused by app, admin and functions.
 */
export function resolveRefereePaymentsConfig(
  tenantConfig?: Pick<TenantConfig, 'refereePaymentsConfig'> | null,
  season?: Pick<BaseSeason, 'refereePaymentsConfig'> | null,
): RefereePaymentsConfig {
  const tenant = tenantConfig?.refereePaymentsConfig;
  const seasonOverride = season?.refereePaymentsConfig;

  return {
    ...DEFAULT_REFEREE_PAYMENTS_CONFIG,
    ...pickDefined(tenant),
    ...pickDefined(seasonOverride),
  };
}

/** Returns a shallow copy without `undefined`/`null` values so they don't
 * clobber lower-priority sources when spread. */
function pickDefined<T extends object>(obj?: Partial<T> | null): Partial<T> {
  if (!obj) return {};
  const out: Partial<T> = {};
  (Object.keys(obj) as (keyof T)[]).forEach((key) => {
    const value = obj[key];
    if (value !== undefined && value !== null) {
      out[key] = value;
    }
  });
  return out;
}

/**
 * Number of matchups (`Game`s) played in a Match, used as the multiplier for the
 * fixed service fee. Counts the acta's `games.length`; returns 0 when `games` is
 * absent/empty. (`match.result` now holds federative points, not a matchup
 * count, so it is no longer a valid fallback.)
 */
export function countMatchGames(match: Pick<Match, 'games'>): number {
  return match.games?.length ?? 0;
}

/**
 * Computes the fixed-amount receipt figures for a Match when
 * `amountSource === 'fixed'` (R53):
 *
 * - `amountService = fixedServiceFee × nMatchups` (per matchup / `Game`);
 * - `amountDisplacement = fixedDisplacementFee` (once per match).
 *
 * Pure function reused by `ensureRefereeReceiptForMatch` (auto) and
 * `generateProxyPaymentLink` (manual club_pays+fixed) so the per-matchup formula
 * lives in one place (R53b). NOT used for `referee_input` (R53c).
 */
export function computeFixedReceiptAmounts(
  match: Pick<Match, 'games'>,
  config: Pick<RefereePaymentsConfig, 'fixedServiceFee' | 'fixedDisplacementFee'>,
): { amountService: number; amountDisplacement: number; matchups: number } {
  const matchups = countMatchGames(match);
  return {
    amountService: (config.fixedServiceFee ?? 0) * matchups,
    amountDisplacement: config.fixedDisplacementFee ?? 0,
    matchups,
  };
}

/**
 * Computes the retention and net amounts for a referee receipt using the
 * resolved config. Retention is applied per concept according to the
 * `retainService` / `retainDisplacement` flags and the resolved `retentionRate`
 * (never a hardcoded 15%). Shared by app desglose, admin SEPA export and CF PDF.
 */
export function computeRefereeReceiptAmounts(
  config: Pick<
    RefereePaymentsConfig,
    'retentionRate' | 'retainService' | 'retainDisplacement'
  >,
  amountService: number,
  amountDisplacement: number,
): {
  gross: number;
  retention: number;
  net: number;
} {
  const rate = (config.retentionRate ?? 0) / 100;
  const service = amountService ?? 0;
  const displacement = amountDisplacement ?? 0;

  const serviceRetention = config.retainService ? service * rate : 0;
  const displacementRetention = config.retainDisplacement
    ? displacement * rate
    : 0;

  const gross = service + displacement;
  const retention = serviceRetention + displacementRetention;

  return {
    gross,
    retention,
    net: gross - retention,
  };
}
